import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { useBasePath } from "@/hooks/useBasePath";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Plus, Search, Users, Eye, Mail, FileText } from "lucide-react";
import { format } from "date-fns";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_STYLES = {
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  in_progress: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  submitted: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  complete: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

export default function AdminInvestors() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { fundId: portalFundId } = useParams();
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterFund, setFilterFund] = useState(portalFundId || "all");

  const basePath = useBasePath();

  const loadInvestors = useCallback(async () => {
    try {
      const url = portalFundId ? `/investors?fund_id=${portalFundId}` : "/investors";
      const { data } = await API.get(url);
      setInvestors(data);
    } catch {
      toast.error("Failed to load investors");
    } finally {
      setLoading(false);
    }
  }, [portalFundId]);

  useEffect(() => { loadInvestors(); }, [loadInvestors]);

  const filtered = investors.filter((inv) => {
    if (filterFund !== "all" && inv.fund_id !== filterFund) return false;
    // Trim leading/trailing whitespace before matching so "somaiya",
    // "somaiya ", and " somaiya " return identical results.
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return inv.name?.toLowerCase().includes(q) || inv.email?.toLowerCase().includes(q);
  });

  const { sorted: sortedInvestors, sortKey, sortDir, toggleSort } = useSortableTable(filtered);

  // Get unique funds for filter
  const uniqueFunds = [...new Map(investors.map(inv => [inv.fund_id, { id: inv.fund_id, name: inv.fund_name || inv.fund_id }]).filter(([k]) => k)).values()];

  const stats = [
    { label: "Total Investors", value: investors.length, color: "text-emerald-500 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
    { label: "Onboarding Complete", value: investors.filter(i => i.onboarding_status === "complete").length, color: "text-blue-500 dark:text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
    { label: "Submitted", value: investors.filter(i => i.onboarding_status === "submitted").length, color: "text-violet-500 dark:text-violet-400", bg: "bg-violet-500/10 border-violet-500/20" },
    { label: "Pending", value: investors.filter(i => i.onboarding_status === "pending").length, color: "text-amber-500 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-investors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Investors</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage investor onboarding and profiles</p>
        </div>
        <Button onClick={() => navigate(`${basePath}/investors/add`)} className="shadow-[0_0_20px_rgba(108,71,196,0.2)]" data-testid="add-investor-button">
          <Plus className="w-4 h-4 mr-2" /> Add Investor
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className={`stat-card ${s.bg}`}>
            <CardContent className="p-5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <p className={`font-heading text-2xl font-bold mt-1 ${s.color}`}>
                {loading ? <span className="inline-block w-8 h-6 bg-secondary rounded animate-pulse" /> : s.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="investors-table-card">
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search investors..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-secondary/50 h-10" data-testid="investor-search-input" />
            </div>
            {uniqueFunds.length > 1 && (
              <Select value={filterFund} onValueChange={setFilterFund}>
                <SelectTrigger className="bg-secondary/40 border-border/50 h-10 text-sm w-52" data-testid="filter-fund-select"><SelectValue placeholder="All Funds" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Funds</SelectItem>
                  {uniqueFunds.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <SortableHead sortKey="name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Name</SortableHead>
                <SortableHead sortKey="email" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Email</SortableHead>
                <SortableHead sortKey="fund_name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Fund</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Distribution</TableHead>
                <SortableHead sortKey="onboarding_status" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Onboarding</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Docs</TableHead>
                <SortableHead sortKey="created_at" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Created</SortableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right pr-6">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2, 3].map((i) => (
                  <TableRow key={i}><TableCell colSpan={8} className="pl-6"><div className="h-10 bg-secondary/30 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
                    <p className="text-muted-foreground text-sm">{search ? "No investors match" : "No investors yet. Add one to get started."}</p>
                  </TableCell>
                </TableRow>
              ) : sortedInvestors.map((inv) => (
                <TableRow key={inv.id} className="table-row-interactive cursor-pointer" onClick={() => navigate(`${basePath}/investors/${inv.id}`)} data-testid={`investor-row-${inv.id}`}>
                  <TableCell className="pl-6 font-medium">{inv.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{inv.email}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{inv.fund_name || "-"}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Mail className="w-3.5 h-3.5" /> {inv.distribution_emails?.length || 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_STYLES[inv.onboarding_status] || ""}`}>
                      {inv.onboarding_status?.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const docs = inv.documents || [];
                      const total = docs.length;
                      const uploaded = docs.filter(d => d.status === "uploaded").length;
                      if (total === 0) return <span className="text-xs text-muted-foreground">-</span>;
                      return (
                        <div className="flex items-center gap-1.5" data-testid={`docs-progress-${inv.id}`}>
                          <FileText className="w-3 h-3 text-muted-foreground" />
                          <span className={`text-xs font-medium ${uploaded === total ? "text-emerald-400" : "text-muted-foreground"}`}>
                            {uploaded}/{total}
                          </span>
                          <div className="w-12 h-1 rounded-full bg-secondary overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${uploaded === total ? "bg-emerald-400" : "bg-amber-400"}`} style={{ width: `${(uploaded / total) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {inv.created_at ? format(new Date(inv.created_at), "MMMM d, yyyy") : "—"}
                  </TableCell>
                  <TableCell className="text-right pr-6" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="w-8 h-8" onClick={() => navigate(`${basePath}/investors/${inv.id}`)} data-testid={`view-investor-${inv.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
